import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useProfile } from "@/hooks/useProfile";
import { usePublications } from "@/hooks/usePublications";
import { NeuralBackground } from "@/components/NeuralBackground";
import { WordReveal } from "@/components/WordReveal";
import { getVisiblePublicationCount } from "@/sections/About";
import { assetPath } from "@/lib/assets";

export function Hero() {
  const { data } = useProfile();
  const { data: publications } = usePublications();

  const name = data?.name ?? "Dr. Per-Arne Andersen";
  const tagline =
    data?.tagline ??
    "Kunstig intelligens for samfunnsnytte — fra algoritmer til ansvarlig bruk i virkelige organisasjoner.";
  const role =
    data?.role ?? "Førsteamanuensis — Institutt for informasjonssystemer, UiA Kristiansand";
  const publicationCount = getVisiblePublicationCount(
    data?.stats.publications,
    publications?.length,
  );
  const studentsSupervised = data?.stats.students_supervised ?? 123;
  const yearsResearching = data?.stats.years_researching ?? 10;

  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const portraitY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);
  const portraitFade = useTransform(scrollYProgress, [0, 1], [1, 0.55]);

  return (
    <section id="top" ref={heroRef} className="relative overflow-hidden">
      <NeuralBackground />
      <TopBar />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-end gap-10 px-6 pb-16 pt-6 md:px-10 lg:grid-cols-[minmax(280px,0.78fr)_minmax(0,1.12fr)] lg:gap-14 lg:px-16 lg:pb-24">
        <motion.div
          className="hero-portrait-wrap order-2 flex min-h-[19rem] items-end justify-start pl-2 pr-10 md:min-h-[30rem] md:pl-6 lg:order-1 lg:min-h-[39rem] lg:pl-0 lg:pr-6"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1.1, ease: "easeOut" }}
          style={{ y: portraitY, opacity: portraitFade }}
        >
          <img
            src={assetPath("/profile/per-arne-andersen.png")}
            alt="Portrett av Dr. Per-Arne Andersen"
            className="hero-portrait w-full max-w-[18rem] object-contain object-left-bottom md:max-w-[25rem] lg:max-w-[28rem] xl:max-w-[30rem]"
          />
        </motion.div>

        <div className="order-1 pt-6 lg:order-2 lg:pt-0">
          <motion.p
            className="font-mono text-xs uppercase tracking-widest text-muted"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {role}
          </motion.p>

          <WordReveal
            as="h1"
            className="mt-5 block max-w-5xl font-display text-5xl font-normal leading-[1.02] tracking-tight md:text-6xl lg:text-7xl"
            stagger={0.06}
            delay={0.05}
          >
            {name}
          </WordReveal>

          <WordReveal
            as="p"
            className="text-fg/85 mt-8 block max-w-prose text-xl leading-relaxed md:text-2xl"
            stagger={0.014}
            delay={0.6}
          >
            {tagline}
          </WordReveal>

          <motion.div
            className="mt-10 flex flex-wrap items-center gap-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.1 }}
          >
            <a
              href="#speaking"
              className="inline-flex items-center gap-3 border border-accent bg-accent px-5 py-3 font-mono text-xs uppercase tracking-widest text-bg transition-colors hover:bg-transparent hover:text-accent focus-visible:bg-transparent focus-visible:text-accent focus-visible:outline-none"
            >
              <span>Bestill foredrag</span>
              <span aria-hidden>→</span>
            </a>
            <a
              href="#publications"
              className="inline-flex items-center gap-3 border border-line px-5 py-3 font-mono text-xs uppercase tracking-widest text-muted transition-colors hover:border-fg hover:text-fg focus-visible:border-fg focus-visible:text-fg focus-visible:outline-none"
            >
              Publikasjoner
            </a>
            <a
              href={assetPath("/cv.pdf")}
              className="inline-flex items-center gap-3 border border-line px-5 py-3 font-mono text-xs uppercase tracking-widest text-muted transition-colors hover:border-fg hover:text-fg focus-visible:border-fg focus-visible:text-fg focus-visible:outline-none"
            >
              CV / PDF
            </a>
          </motion.div>

          <motion.dl
            className="mt-12 grid max-w-2xl grid-cols-1 gap-4 border-t border-line pt-6 sm:grid-cols-3 sm:gap-x-8"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.3 }}
            aria-label="Karriere i tall"
          >
            <HeroStat label="Publikasjoner" value={publicationCount} approximate />
            <HeroStat label="Veiledede studenter" value={studentsSupervised} approximate />
            <HeroStat label="Forskningsår" value={yearsResearching} />
          </motion.dl>
        </div>
      </div>

      <PersonSchema name={name} role={role} />
    </section>
  );
}

const NAV: ReadonlyArray<{ label: string; href: string }> = [
  { label: "Om", href: "#about" },
  { label: "Foredrag", href: "#speaking" },
  { label: "Undervisning", href: "#teaching" },
  { label: "Publikasjoner", href: "#publications" },
  { label: "Prosjekter", href: "#projects" },
];

function TopBar() {
  return (
    <nav
      aria-label="Navigasjon"
      className="relative z-10 mx-auto flex w-full max-w-7xl flex-col items-start justify-between gap-4 px-6 py-5 md:flex-row md:items-center md:gap-6 md:px-10 lg:px-16"
    >
      <a
        href="#top"
        className="font-mono text-xs uppercase tracking-widest text-muted hover:text-fg focus-visible:text-fg focus-visible:outline-none"
      >
        per-arne.no
      </a>
      <ul className="hidden flex-wrap justify-end gap-x-5 gap-y-2 font-mono text-[0.65rem] uppercase tracking-widest text-muted sm:flex md:text-xs">
        {NAV.map(({ label, href }) => (
          <li key={href}>
            <a
              href={href}
              className="hover:text-fg focus-visible:text-fg focus-visible:outline-none"
            >
              {label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function HeroStat({
  label,
  value,
  approximate = false,
}: {
  label: string;
  value: number;
  approximate?: boolean;
}) {
  return (
    <div>
      <dt className="font-mono text-[0.6rem] uppercase tracking-widest text-muted">{label}</dt>
      <dd className="mt-1 font-display text-3xl font-normal text-fg md:text-4xl">
        {approximate && (
          <span aria-hidden className="text-muted">
            ~
          </span>
        )}
        {value}
      </dd>
    </div>
  );
}

function PersonSchema({ name, role }: { name: string; role: string }) {
  const displayName = name.replace(/^Dr\.\s+/u, "");
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Person",
          name: displayName,
          honorificPrefix: "Dr.",
          jobTitle: "Førsteamanuensis",
          image: "https://per-arne.no/profile/per-arne-andersen.png",
          email: "mailto:per.andersen@uia.no",
          affiliation: {
            "@type": "CollegeOrUniversity",
            name: "Universitetet i Agder",
          },
          worksFor: {
            "@type": "CollegeOrUniversity",
            name: "Universitetet i Agder",
          },
          sameAs: [
            "https://orcid.org/0000-0002-7742-4907",
            "https://scholar.google.no/citations?user=oPth46oAAAAJ",
            "https://www.semanticscholar.org/author/Per-Arne-Andersen/33999307",
            "https://www.uia.no/english/about-uia/employees/perara12/",
          ],
          description: role,
          url: "https://per-arne.no/",
        }),
      }}
    />
  );
}
